import { plural, t } from "@lingui/core/macro";
import { includes, some } from "lodash/fp";
import { DateTime } from "luxon";

import Config from "@readai/common/config";
import { UNKNOWN_PLAN } from "@readai/common/constants/billing";
import { INTEGRATION_PROVIDERS_BY_ID } from "@readai/common/constants/providers";
import {
  BillingCurrentPlan,
  BillingInterval,
  BillingPlan,
  BillingScheduledPlan,
  BillingSubscription,
  CurrentSubscriptionPlan,
  IntegrationProviderId,
  PremiumIntegrationAccessSetting,
  StandardSubscriptionPlan,
  SubscriptionPlan,
  User,
} from "@readai/common/types";
import { optionalParseUTCDateTimeFromString } from "@readai/common/utils/datetimeUtils";

const FREE_PLANS: BillingPlan[] = ["free", "zoom_bundle_trial"];

export const PLANS_IN_ASC_ORDER: BillingPlan[] = [
  "free",
  "zoom_bundle_trial",
  "pro_edu",
  "enterprise_edu",
  "pro",
  "enterprise",
  "enterprise_plus",
];

export const INTERVALS_IN_ASC_ORDER: BillingInterval[] = [
  "day",
  "week",
  "month",
  "year",
];

export const getPlanLabel = (
  plan?: BillingPlan,
  opts?: { variant: "long" | "short" },
): string => {
  const billingPlan = plan ?? "free";

  switch (billingPlan) {
    case "free":
      return t`Free`;
    case "zoom_bundle_trial":
      return opts?.variant === "short"
        ? t`Zoom Apps`
        : t`Essential Apps via Zoom`;
    case "pro_edu":
      return t`EDU`;
    case "pro":
      return t`Pro`;
    case "enterprise_edu":
      return t`EDU + Video`;
    case "enterprise":
      return t`Enterprise`;
    case "enterprise_plus":
      return t`Enterprise+`;
    case "unknown":
    default:
      return "";
  }
};

export const getRemainingTrialDays = (
  trialEndDate?: string,
  now: DateTime = DateTime.now(),
): number => {
  const trialEnd = optionalParseUTCDateTimeFromString(trialEndDate);
  if (!trialEnd) return 0;

  const numDays = trialEnd.diff(now, "days").as("days");
  return Math.max(0, Math.round(numDays));
};

export const isUnknownPlan = (plan: BillingPlan) => plan === "unknown";

export const isEduPlan = (plan: BillingPlan) =>
  includes(plan, ["pro_edu", "enterprise_edu"]);

export const getCurrentPlanFromSubscriptionAndUser = (
  subscription: BillingSubscription,
  user: User | null,
): BillingCurrentPlan => {
  if (isUnknownPlan(subscription.plan)) {
    const billingAccountPlan = user?.billingAccount
      ? user.billingAccount.plan
      : user
        ? "free" // if there is a user but no billing account, this user is on the free plan
        : null;

    if (billingAccountPlan) {
      // if we're having trouble getting the subscription from the BE, use the plan
      // from the user's billing account as a fallback. this will always be the same
      // plan that's returned on the subscription (though is more likely to be stale, as we
      // don't fetch the user's billing account as frequently as we do the subscription).
      // we also won't know the interval details (if the user is on a paid plan) or the
      // actual seat maximum, so we will assume the user is at their seat maximum until we can
      // definitively determine from the BE (via their subscription) that they are not

      return {
        type: billingAccountPlan,
        intervalCost: 0,
        intervalCount: 0,
        label: getPlanLabel(billingAccountPlan),
        seatMaximum: user?.billingAccount?.billableSeats ?? 1,
      };
    }

    return UNKNOWN_PLAN;
  }

  return {
    type: subscription.plan,
    interval: subscription.interval,
    intervalCost: subscription.intervalCost ?? 0,
    intervalCount: subscription.intervalCount ?? 0,
    label: getPlanLabel(subscription.plan),
    seatMaximum: subscription.settings.workspaceMemberLimit,
    stripePriceId: subscription.stripePriceId,
  };
};

export const getScheduledPlanFromSubscription = (
  subscription: BillingSubscription,
): BillingScheduledPlan | undefined => {
  if (!subscription.scheduledUpdate) return;

  return {
    type: subscription.scheduledUpdate.plan,
    label: getPlanLabel(subscription.scheduledUpdate.plan),
    interval: subscription.scheduledUpdate.interval,
    intervalCost: subscription.scheduledUpdate.intervalCost ?? 0,
    intervalCount: subscription.scheduledUpdate.intervalCount ?? 0,
    stripePriceId: subscription.scheduledUpdate.stripePriceId,
  };
};

export const getIntervalDescription = (
  interval: BillingInterval,
  intervalCount: number,
) => {
  if (intervalCount === 1) {
    return getIntervalLabel(interval, { capitalize: true });
  }

  const intervalDuration = getIntervalDuration({ interval, intervalCount });

  if (!intervalDuration) return "";

  return t`Every ${intervalDuration}`;
};

export const getIntervalDuration = (
  plan: Pick<SubscriptionPlan, "interval" | "intervalCount">,
) => {
  if (!plan.intervalCount) return null;

  switch (plan.interval) {
    case "day":
      return plural(plan.intervalCount, { one: "# day", other: "# days" });
    case "week":
      return plural(plan.intervalCount, { one: "# week", other: "# weeks" });
    case "month":
      return plural(plan.intervalCount, { one: "# month", other: "# months" });
    case "year":
      return plural(plan.intervalCount, { one: "# year", other: "# years" });
    default:
      return null;
  }
};

export const getIntervalLabel = (
  interval: BillingInterval,
  opts?: {
    asAdverb?: boolean;
    capitalize?: boolean;
  },
) => {
  switch (interval) {
    case "day":
      return opts?.capitalize ? t`Daily` : t`daily`;
    case "week":
      return opts?.capitalize ? t`Weekly` : t`weekly`;
    case "month":
      return opts?.capitalize ? t`Monthly` : t`monthly`;
    case "year":
      // this is the only one where the adverb and the non-duration variant are different
      return opts?.asAdverb
        ? opts.capitalize
          ? t`Annually`
          : t`annually`
        : opts?.capitalize
          ? t`Annual`
          : t`annual`;
    default:
      return "";
  }
};

export const getMonthlyPlanPrice = (plan: StandardSubscriptionPlan) => {
  switch (plan.interval) {
    case "month":
      return plan.intervalCost;
    case "year":
      return plan.intervalCost / 12;
    default:
      return 0;
  }
};

export const getMaximumLicensesForTransaction = (intervalCost: number) => {
  // assume a pretty generous sales tax to be sure that even after tax is added,
  // the transaction will not go over the limit
  const maxUntaxedPurchaseAmount = Config.billing.maximumPurchaseAmount * 0.8;
  return Math.floor(maxUntaxedPurchaseAmount / intervalCost);
};

export const planSupportsVideoRecording = (
  subscriptionPlan: CurrentSubscriptionPlan,
) => subscriptionPlan.subscriptionSettings.storeRecording;

export const planSupportsIntegration = (
  subscriptionPlan: CurrentSubscriptionPlan,
  providerId: IntegrationProviderId,
) => {
  const provider = INTEGRATION_PROVIDERS_BY_ID[providerId];
  if (!provider.isPremium) return true;

  const accessSettings = INTEGRATION_PROVIDERS_BY_ID[providerId]
    .accessSettings as PremiumIntegrationAccessSetting[];

  return some(
    (setting) => subscriptionPlan.subscriptionSettings[setting],
    accessSettings,
  );
};

export const isFreePlan = (
  plan: BillingPlan,
  exact = false,
): plan is (typeof FREE_PLANS)[number] => {
  if (exact) return plan === "free";
  return FREE_PLANS.includes(plan);
};

export const isZoomPlan = (plan: BillingPlan) => plan === "zoom_bundle_trial";

export const isPaidPlan = (plan: BillingPlan) =>
  !isFreePlan(plan) && plan !== "unknown";
